import { datadogRum } from "@datadog/browser-rum";
import { TelemetryLevel } from "../utils/telemetryLevel";
import { hasConsent } from "../../Consent/state/consentStore";

/**
 * Applies a telemetry level to the running web SDK (DHT-5279), combined with
 * the visitor's analytics consent (GDPR opt-in brands, CONSENT.md).
 *
 * Collection needs BOTH: a level other than `off`, and analytics consent. In
 * NotRequired mode consent is always granted, so this is the level alone, as
 * before. Called by `useTelemetryGate` when the level changes and by the
 * consent sync in `init.web.ts` when consent changes, and it reads consent
 * itself so the two callers cannot disagree.
 *
 * `bootLevel` is what `init` configured at init, and it decides whether
 * Session Replay can be resumed in THIS session.
 *
 * Replay sampling is drawn once per session and stored in the session cookie,
 * so a session that booted without replay stays without it. The SDK does offer
 * `startSessionReplayRecording({ force: true })` to override that, and we
 * deliberately do not use it: forcing would defeat `sessionReplaySampleRate`
 * for every user the moment that rate is lowered below 100. Turning telemetry
 * back up therefore restores replay on the next load rather than instantly,
 * which is the safe direction to be wrong in.
 *
 * A visitor who booted without consent is different: `init` started the
 * SDK with `startSessionReplayRecordingManually`, so the sample is drawn but the
 * recorder waits, and the start below is what begins it once consent arrives.
 */
export const applyTelemetryLevel = (
  level: TelemetryLevel,
  bootLevel: TelemetryLevel,
): void => {
  try {
    const collecting = level !== "off" && hasConsent("analytics");

    datadogRum.setTrackingConsent(collecting ? "granted" : "not-granted");

    if (collecting && level === "full") {
      // Only meaningful if this session was drawn with replay. When it was not,
      // this is a no-op and recording resumes after a reload.
      if (bootLevel === "full") datadogRum.startSessionReplayRecording();
      return;
    }

    datadogRum.stopSessionReplayRecording();
  } catch {
    // The SDK is not initialised (a boot failure, or a test), and there is
    // nothing to gate. Never let telemetry configuration break a render.
  }
};
